/** UI strings for the F05 models (matter waves, detections, wave packets); merged into the photons dictionary. */
export const sv = {
  mwTitle: "Materievågor: de Broglie-våglängd och diffraktion",
  mwNote:
    "λ = h/p för elektron, proton, neutron, alfapartikel och foton. Heldragna kurvor är exakta (relativistiska), den streckade är den icke-relativistiska formeln för den valda partikeln. Fyllda och ofyllda punkter visar partiklar med samma fart, rörelsemängd eller kinetiska energi.",
  mwViewWavelength: "Våglängd mot energi",
  mwViewDiffraction: "Diffraktion",
  mwSpecies: "Partikel",
  mwElectron: "Elektron",
  mwProton: "Proton",
  mwNeutron: "Neutron",
  mwAlpha: "Alfapartikel",
  mwPhoton: "Foton",
  mwKinetic: "Kinetisk energi K, för fotonen E (eV)",
  mwHold: "Jämför vid",
  mwHoldSpeed: "Samma fart",
  mwHoldMomentum: "Samma rörelsemängd",
  mwHoldKinetic: "Samma kinetiska energi",
  mwLattice: "Spridare",
  mwSurface: "Ytrader (Davisson–Germer)",
  mwBragg: "Kristallplan (Braggs lag)",
  mwSpacingA: "Radavstånd a (nm)",
  mwSpacingD: "Planavstånd d (nm)",
  mwPc: "pc (eV)",
  mwMomentum: "p (kg m/s)",
  mwSpeed: "v/c (exakt)",
  mwSpeedClassical: "v/c icke-relativistiskt √(2K/mc²)",
  mwLambda: "λ = h/p (nm)",
  mwLambdaClassical: "λ icke-relativistiskt h/√(2mK) (nm)",
  mwClassicalError: "Fel i den icke-relativistiska λ (%)",
  mwPhotonSame: "Foton med samma λ: E = hc/λ (eV)",
  mwCompareRow: "{name}",
  mwCompareValue: "λ = {lambda} nm, K = {kinetic} eV",
  mwCompareNone: "— (fotonen har alltid farten c)",
  mwOrdersSurface: "θ från normalen för n = 1, 2, … (°)",
  mwOrdersBragg: "Braggvinkel θ mot planen för n = 1, 2, … (°)",
  mwTwoTheta: "Spridningsvinkel 2θ (°)",
  mwRatio: "λ/a respektive λ/(2d)",
  mwNoOrder: "ingen",
  mwNoOrderSurface: "Ingen ordning: λ > a, så sin θ = λ/a > 1. Öka energin eller radavståndet.",
  mwNoOrderBragg: "Ingen ordning: λ > 2d, så sin θ = λ/(2d) > 1. Öka energin eller planavståndet.",
  mwPlotSvg: "de Broglie-våglängd mot kinetisk energi",
  mwPlotDesc:
    "Log–log-diagram med kinetisk energi från 10⁻³ till 10⁷ eV och våglängd från 10⁻⁶ till 10³ nm. En kurva per partikelslag, fotonen med lutning −1 och massiva partiklar med lutning −1/2 vid låg energi som övergår i fotonens kurva vid hög energi. Punkterna markerar de jämförda partiklarna.",
  mwAxisKinetic: "K (eV)",
  mwAxisLambda: "λ (nm)",
  mwDiffractionSvg: "Spridningsgeometri",
  mwSurfaceDesc:
    "Strålen faller vinkelrätt mot en rad av ytatomer. Spridda strålar för ordningarna n = 1, 2, … visas symmetriskt kring normalen, med vinkeln θ₁ markerad.",
  mwBraggDesc:
    "Strålen faller in mot parallella kristallplan med Braggvinkeln (glansvinkeln) θ och reflekteras med samma vinkel. Den streckade linjen visar den ospridda riktningen; vinkeln mellan den och den reflekterade strålen är 2θ. Endast första ordningen ritas.",
  mwThetaOne: "θ₁ = {theta}°",
  mwLambdaLabel: "λ = {lambda} nm",
  mwFormula:
    "$\\lambda = h/p$, $pc = \\sqrt{K(K+2mc^2)}$, så $\\lambda = hc/\\sqrt{K(K+2mc^2)}$; för $K \\ll mc^2$ blir det $h/\\sqrt{2mK}$.",
  mwDiffractionFormula:
    "Ytrader: $a\\sin\\theta = n\\lambda$ med $\\theta$ från normalen. Kristallplan: $2d\\sin\\theta = n\\lambda$ med $\\theta$ mot planen och spridningsvinkeln $2\\theta$.",

  detTitle: "En detektion i taget",
  detNote:
    "Modell, inte mätdata: varje punkt är en simulerad detektion dragen ur P(q) ∝ exp(−q²/5)·cos²(3q) med fast frö. Histogrammet använder alla N detektioner.",
  detCountSlider: "Antal detektioner N",
  detBins: "Antal staplar",
  detScale: "Skala",
  detRelative: "Relativ frekvens n/N",
  detDensity: "Täthet n/(NΔq)",
  detCurve: "Modellkurva P(q)",
  detReseed: "Nytt frö",
  detSvg: "Detektioner och histogram",
  detDesc:
    "Överst varje detektion som en punkt längs skärmen (lodrät spridning bara för läsbarhet). Nederst ett histogram över samma detektioner, eventuellt med modellens sannolikhetstäthet inritad.",
  detAxisQ: "läge på skärmen q",
  detAxisDensity: "n/(NΔq)",
  detAxisRelative: "n/N",
  detClipped: "Punkterna visar de första {shown} detektionerna; histogrammet använder alla.",
  detCount: "N",
  detWidth: "Stapelbredd Δq",
  detFullest: "Flest träffar i en stapel, n",
  detFluctuation: "Relativ fluktuation 1/√n",
  detCentral: "Andel i centrala maximum |q| < π/6",
  detCentralModel: "Modellens sannolikhet för |q| < π/6",
  detChi2: "χ² per stapel (staplar med minst 5 väntade)",
  detChi2None: "för få detektioner",
  detSeed: "Frö",
  detFormula:
    "Borns sannolikhetstolkning: $P(a \\le q \\le b) = \\int_a^b |\\psi|^2\\,dq$. En stapel med $n$ träffar fluktuerar ungefär med $\\sqrt{n}$, så den relativa osäkerheten är $1/\\sqrt{n}$.",

  wpTitle: "Vågpaket: osäkerhet, grupphastighet och spridning",
  wpNote:
    "Fri elektron. Vy Vågpaket: Re ψ och höljet ±|ψ| på fasta axlar i enheter av σ₀ kring höljets mitt, och |φ(k)|² som funktion av (k − k₀)σ₀. Vy Flygtid: bredden efter flygtiden T som funktion av startbredden.",
  wpViewPacket: "Vågpaket",
  wpViewFlight: "Flygtid",
  wpShape: "Tillstånd",
  wpGauss: "Gaussiskt (minimalt)",
  wpXGauss: "x·Gauss (inte minimalt)",
  wpSigma: "σ₀ (nm)",
  wpWaveNumber: "k₀σ₀",
  wpElapsed: "Tid t/t₀",
  wpHalve: "Halvera σ₀",
  wpDouble: "Fördubbla σ₀",
  wpK0: "k₀ (nm⁻¹)",
  wpLambda0: "λ₀ = 2π/k₀ (nm)",
  wpKinetic: "K = ħ²k₀²/(2m) (eV)",
  wpT0: "t₀ = 2mσ₀²/ħ (fs)",
  wpTime: "t (fs)",
  wpSigmaT: "σ(t) = σ₀√(1 + (t/t₀)²) (nm)",
  wpDeltaX: "Δx vid t (nm)",
  wpDeltaP: "Δp = ħΔk (kg m/s)",
  wpProduct0: "Δx Δp/ħ vid t = 0",
  wpProductT: "Δx Δp/ħ vid t",
  wpGroup: "Grupphastighet v_g = ħk₀/m (m/s)",
  wpPhase: "Fashastighet v_fas = ω₀/k₀ (m/s)",
  wpPacketSvg: "Vågpaket i läge och i vågtal",
  wpPacketDesc:
    "Övre panelen: realdelen av ψ och höljet ±|ψ| mellan −6σ₀ och 6σ₀ kring höljets mitt, med lodräta streckade linjer vid ±Δx och en heldragen linje där bärvågens fas k₀x − ω₀t är noll. Nedre panelen: |φ(k)|² mellan −3 och 3 i (k − k₀)σ₀ med streckade linjer vid ±Δk.",
  wpAxisX: "(x − v_g t)/σ₀",
  wpAxisK: "(k − k₀)σ₀",
  wpAxisPsi: "Re ψ, ±|ψ|",
  wpAxisPhi: "|φ(k)|²",
  wpParticle: "Partikel",
  wpElectron: "Elektron",
  wpMarble: "Kula 20 g",
  wpFlightTime: "Flygtid T (s)",
  wpRatio: "Startbredd σ₀ / optimum",
  wpDetector: "Detektorns rms-bredd (μm)",
  wpOptimal: "Optimal startbredd",
  wpMarbleButton: "Kulan faller 25 m",
  wpMass: "m (kg)",
  wpFlightTimeRow: "T (s)",
  wpOptimum: "Optimal σ₀ = √(ħT/2m) (m)",
  wpMinimum: "Minsta σ_T för okorrelerad gaussisk start, √(ħT/m) (m)",
  wpSigma0: "Vald σ₀ (m)",
  wpSigmaFinal: "σ_T (m)",
  wpSigmaRatio: "σ_T / minsta σ_T",
  wpObserved: "Observerad bredd √(σ_T² + σ_det²) (m)",
  wpObservedMinimum: "Minsta observerade bredd (m)",
  wpFlightSvg: "Slutbredd mot startbredd",
  wpFlightDesc:
    "Log–log-diagram: slutbredden σ_T i enheter av sitt minimum mot startbredden i enheter av optimum. Den röda kurvan har minimum 1 vid 1. Streckade asymptoter visar startbredden själv och spridningen ħT/(2mσ₀). Den blå kurvan lägger till detektorns bredd i kvadratur. Punkten är den valda startbredden.",
  wpAxisRatio: "σ₀ / optimum",
  wpAxisWidth: "bredd / minsta σ_T",
  wpFormula:
    "$\\Delta x = \\sigma(t)$, $\\Delta p = \\hbar/(2\\sigma_0)$ för det gaussiska paketet, så $\\Delta x\\,\\Delta p = \\tfrac{\\hbar}{2}\\sqrt{1+(t/t_0)^2}$. $v_g = d\\omega/dk = \\hbar k_0/m = p/m$ och $v_\\text{fas} = \\omega/k = v_g/2$.",
  wpFlightFormula:
    "$\\sigma_T^2 = \\sigma_0^2 + \\left(\\frac{\\hbar T}{2m\\sigma_0}\\right)^2$, minimum $\\sqrt{\\hbar T/m}$ vid $\\sigma_0 = \\sqrt{\\hbar T/(2m)}$; en detektor med rms-bredden $\\sigma_\\text{det}$ ger $\\sqrt{\\sigma_T^2 + \\sigma_\\text{det}^2}$.",
};

export const en: Record<keyof typeof sv, string> = {
  mwTitle: "Matter waves: de Broglie wavelength and diffraction",
  mwNote:
    "λ = h/p for electron, proton, neutron, alpha particle and photon. Solid curves are exact (relativistic); the dashed curve is the non-relativistic formula for the chosen particle. Filled and open dots show particles with the same speed, momentum or kinetic energy.",
  mwViewWavelength: "Wavelength vs energy",
  mwViewDiffraction: "Diffraction",
  mwSpecies: "Particle",
  mwElectron: "Electron",
  mwProton: "Proton",
  mwNeutron: "Neutron",
  mwAlpha: "Alpha particle",
  mwPhoton: "Photon",
  mwKinetic: "Kinetic energy K, for the photon E (eV)",
  mwHold: "Compare at",
  mwHoldSpeed: "Same speed",
  mwHoldMomentum: "Same momentum",
  mwHoldKinetic: "Same kinetic energy",
  mwLattice: "Scatterer",
  mwSurface: "Surface rows (Davisson–Germer)",
  mwBragg: "Crystal planes (Bragg's law)",
  mwSpacingA: "Row spacing a (nm)",
  mwSpacingD: "Plane spacing d (nm)",
  mwPc: "pc (eV)",
  mwMomentum: "p (kg m/s)",
  mwSpeed: "v/c (exact)",
  mwSpeedClassical: "v/c non-relativistic √(2K/mc²)",
  mwLambda: "λ = h/p (nm)",
  mwLambdaClassical: "λ non-relativistic h/√(2mK) (nm)",
  mwClassicalError: "Error of the non-relativistic λ (%)",
  mwPhotonSame: "Photon with the same λ: E = hc/λ (eV)",
  mwCompareRow: "{name}",
  mwCompareValue: "λ = {lambda} nm, K = {kinetic} eV",
  mwCompareNone: "— (the photon always moves at c)",
  mwOrdersSurface: "θ from the normal for n = 1, 2, … (°)",
  mwOrdersBragg: "Glancing angle θ to the planes for n = 1, 2, … (°)",
  mwTwoTheta: "Scattering angle 2θ (°)",
  mwRatio: "λ/a or λ/(2d)",
  mwNoOrder: "none",
  mwNoOrderSurface: "No order: λ > a, so sin θ = λ/a > 1. Raise the energy or the row spacing.",
  mwNoOrderBragg: "No order: λ > 2d, so sin θ = λ/(2d) > 1. Raise the energy or the plane spacing.",
  mwPlotSvg: "de Broglie wavelength vs kinetic energy",
  mwPlotDesc:
    "Log–log plot with kinetic energy from 10⁻³ to 10⁷ eV and wavelength from 10⁻⁶ to 10³ nm. One curve per species: the photon with slope −1, massive particles with slope −1/2 at low energy, merging into the photon curve at high energy. Dots mark the compared particles.",
  mwAxisKinetic: "K (eV)",
  mwAxisLambda: "λ (nm)",
  mwDiffractionSvg: "Scattering geometry",
  mwSurfaceDesc:
    "The beam falls normally on a row of surface atoms. Scattered beams for orders n = 1, 2, … are drawn symmetrically about the normal, with θ₁ marked.",
  mwBraggDesc:
    "The beam meets parallel crystal planes at the glancing angle θ and is reflected at the same angle. The dashed line is the unscattered direction; the angle between it and the reflected beam is 2θ. Only the first order is drawn.",
  mwThetaOne: "θ₁ = {theta}°",
  mwLambdaLabel: "λ = {lambda} nm",
  mwFormula:
    "$\\lambda = h/p$, $pc = \\sqrt{K(K+2mc^2)}$, so $\\lambda = hc/\\sqrt{K(K+2mc^2)}$; for $K \\ll mc^2$ this becomes $h/\\sqrt{2mK}$.",
  mwDiffractionFormula:
    "Surface rows: $a\\sin\\theta = n\\lambda$ with $\\theta$ from the normal. Crystal planes: $2d\\sin\\theta = n\\lambda$ with $\\theta$ to the planes and scattering angle $2\\theta$.",

  detTitle: "One detection at a time",
  detNote:
    "A model, not measured data: every dot is a simulated detection drawn from P(q) ∝ exp(−q²/5)·cos²(3q) with a fixed seed. The histogram uses all N detections.",
  detCountSlider: "Number of detections N",
  detBins: "Number of bins",
  detScale: "Scale",
  detRelative: "Relative frequency n/N",
  detDensity: "Density n/(NΔq)",
  detCurve: "Model curve P(q)",
  detReseed: "New seed",
  detSvg: "Detections and histogram",
  detDesc:
    "Top: every detection as a dot along the screen (vertical scatter only for legibility). Bottom: a histogram of the same detections, optionally with the model probability density drawn in.",
  detAxisQ: "position on the screen q",
  detAxisDensity: "n/(NΔq)",
  detAxisRelative: "n/N",
  detClipped: "The dots show the first {shown} detections; the histogram uses all of them.",
  detCount: "N",
  detWidth: "Bin width Δq",
  detFullest: "Most hits in one bin, n",
  detFluctuation: "Relative fluctuation 1/√n",
  detCentral: "Fraction in the central maximum |q| < π/6",
  detCentralModel: "Model probability for |q| < π/6",
  detChi2: "χ² per bin (bins with at least 5 expected)",
  detChi2None: "too few detections",
  detSeed: "Seed",
  detFormula:
    "Born's probability interpretation: $P(a \\le q \\le b) = \\int_a^b |\\psi|^2\\,dq$. A bin with $n$ hits fluctuates by about $\\sqrt{n}$, so its relative uncertainty is $1/\\sqrt{n}$.",

  wpTitle: "Wave packets: uncertainty, group velocity and spreading",
  wpNote:
    "Free electron. Packet view: Re ψ and the envelope ±|ψ| on fixed axes in units of σ₀ around the envelope centre, and |φ(k)|² against (k − k₀)σ₀. Flight view: the width after the flight time T as a function of the initial width.",
  wpViewPacket: "Wave packet",
  wpViewFlight: "Flight time",
  wpShape: "State",
  wpGauss: "Gaussian (minimal)",
  wpXGauss: "x·Gauss (not minimal)",
  wpSigma: "σ₀ (nm)",
  wpWaveNumber: "k₀σ₀",
  wpElapsed: "Time t/t₀",
  wpHalve: "Halve σ₀",
  wpDouble: "Double σ₀",
  wpK0: "k₀ (nm⁻¹)",
  wpLambda0: "λ₀ = 2π/k₀ (nm)",
  wpKinetic: "K = ħ²k₀²/(2m) (eV)",
  wpT0: "t₀ = 2mσ₀²/ħ (fs)",
  wpTime: "t (fs)",
  wpSigmaT: "σ(t) = σ₀√(1 + (t/t₀)²) (nm)",
  wpDeltaX: "Δx at t (nm)",
  wpDeltaP: "Δp = ħΔk (kg m/s)",
  wpProduct0: "Δx Δp/ħ at t = 0",
  wpProductT: "Δx Δp/ħ at t",
  wpGroup: "Group velocity v_g = ħk₀/m (m/s)",
  wpPhase: "Phase velocity v_ph = ω₀/k₀ (m/s)",
  wpPacketSvg: "Wave packet in position and wave number",
  wpPacketDesc:
    "Top panel: the real part of ψ and the envelope ±|ψ| from −6σ₀ to 6σ₀ around the envelope centre, with dashed lines at ±Δx and a solid line where the carrier phase k₀x − ω₀t is zero. Bottom panel: |φ(k)|² from −3 to 3 in (k − k₀)σ₀ with dashed lines at ±Δk.",
  wpAxisX: "(x − v_g t)/σ₀",
  wpAxisK: "(k − k₀)σ₀",
  wpAxisPsi: "Re ψ, ±|ψ|",
  wpAxisPhi: "|φ(k)|²",
  wpParticle: "Particle",
  wpElectron: "Electron",
  wpMarble: "20 g marble",
  wpFlightTime: "Flight time T (s)",
  wpRatio: "Initial width σ₀ / optimum",
  wpDetector: "Detector rms width (μm)",
  wpOptimal: "Optimal initial width",
  wpMarbleButton: "Marble falls 25 m",
  wpMass: "m (kg)",
  wpFlightTimeRow: "T (s)",
  wpOptimum: "Optimal σ₀ = √(ħT/2m) (m)",
  wpMinimum: "Smallest σ_T for an uncorrelated Gaussian start, √(ħT/m) (m)",
  wpSigma0: "Chosen σ₀ (m)",
  wpSigmaFinal: "σ_T (m)",
  wpSigmaRatio: "σ_T / smallest σ_T",
  wpObserved: "Observed width √(σ_T² + σ_det²) (m)",
  wpObservedMinimum: "Smallest observed width (m)",
  wpFlightSvg: "Final width vs initial width",
  wpFlightDesc:
    "Log–log plot: the final width σ_T in units of its minimum against the initial width in units of the optimum. The red curve has minimum 1 at 1. Dashed asymptotes show the initial width itself and the spreading ħT/(2mσ₀). The blue curve adds the detector width in quadrature. The dot is the chosen initial width.",
  wpAxisRatio: "σ₀ / optimum",
  wpAxisWidth: "width / smallest σ_T",
  wpFormula:
    "$\\Delta x = \\sigma(t)$, $\\Delta p = \\hbar/(2\\sigma_0)$ for the Gaussian packet, so $\\Delta x\\,\\Delta p = \\tfrac{\\hbar}{2}\\sqrt{1+(t/t_0)^2}$. $v_g = d\\omega/dk = \\hbar k_0/m = p/m$ and $v_\\text{ph} = \\omega/k = v_g/2$.",
  wpFlightFormula:
    "$\\sigma_T^2 = \\sigma_0^2 + \\left(\\frac{\\hbar T}{2m\\sigma_0}\\right)^2$, minimum $\\sqrt{\\hbar T/m}$ at $\\sigma_0 = \\sqrt{\\hbar T/(2m)}$; a detector with rms width $\\sigma_\\text{det}$ gives $\\sqrt{\\sigma_T^2 + \\sigma_\\text{det}^2}$.",
};
